import React, { useEffect, useState } from "react";
import { Button, Input, Modal, Select } from "antd";
import { BackendStatusBadge, InfringementTypeBadge, ReviewStatusBadge } from "./status_badges.component";
import { formatDateTime, formatDurationSeconds, formatSpeed, getInfringementActionLabel } from "../services/firestoreTracking.service";

const ACTION_OPTIONS = [
  { value: "called_driver_alert", label: "Called driver due to infringement alert" },
  { value: "called_no_response", label: "Called and no response" },
  { value: "warning_issued", label: "Warning issued" },
  { value: "escalated_to_management", label: "Escalated to management" },
];

const DetailRow = ({ label, value }) => {
  return <div className="rounded-2xl border border-[#ece5dd] bg-[#fcfaf7] px-4 py-4">
    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8c7b6b]">{label}</div>
    <div className="mt-2 text-[1rem] leading-7 text-[#161311]">{value || "Not available"}</div>
  </div>;
};

export const InfringementDetailModal = ({
  record,
  open,
  onCancel,
  onReview,
  onAction,
  saving,
  actionSaving,
  canReview = true,
  canLogAction = true,
  selectedProvinceId,
}) => {
  const [note, setNote] = useState("");
  const [actionNote, setActionNote] = useState("");
  const [actionType, setActionType] = useState("called_driver_alert");

  useEffect(() => {
    setNote(record?.reviewNote || "");
    setActionNote("");
    setActionType("called_driver_alert");
  }, [record?.id, record?.reviewNote]);

  const submit = async (changes) => {
    if (!record?.id || !onReview) return;
    await onReview(record, changes);
  };

  const submitAction = async () => {
    if (!record?.id || !onAction) return;
    await onAction(record, {
      actionType,
      note: actionNote,
      provinceId: selectedProvinceId && selectedProvinceId !== "all" ? selectedProvinceId : record?.originId,
    });
    setActionNote("");
  };

  return <Modal width={1000} footer={null} open={open} onCancel={onCancel} title="Infringement Detail">
    {!record ? null : <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-3">
        <InfringementTypeBadge type={record?.type} />
        <BackendStatusBadge status={record?.status} />
        <ReviewStatusBadge status={record?.reviewStatus} />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <DetailRow label="Trip" value={record?.tripReference || `MYC#${record?.tripOrderId || "Unknown"}`} />
        <DetailRow label="Driver" value={record?.driverName || record?.driverIdentifier} />
        <DetailRow label="Vehicle" value={record?.vehicleRegistration || record?.vehicleLabel} />
        <DetailRow label="Event Time" value={formatDateTime(record?.eventAt)} />
        <DetailRow label="Coordinates" value={record?.coordinatesLabel} />
        <DetailRow label="Confidence" value={Number.isFinite(Number(record?.confidenceValue)) ? Number(record?.confidenceValue).toFixed(2) : "Not available"} />
      </div>

      {record?.type === "speeding" ? <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <DetailRow label="Road" value={record?.roadName || record?.roadAddress} />
        <DetailRow label="Speed Limit" value={formatSpeed(record?.speedLimitKph)} />
        <DetailRow label="Peak Speed" value={formatSpeed(record?.peakSpeedKph || record?.actualSpeedKph)} />
        <DetailRow label="Delta" value={Number.isFinite(Number(record?.deltaKph)) ? `+${Number(record.deltaKph).toFixed(1)} km/h` : "Not available"} />
        <DetailRow label="Trigger Rule" value={Number.isFinite(Number(record?.triggerSpeedKph)) ? `${formatSpeed(record?.speedLimitKph)} +${Number(record?.triggerToleranceKph || 0).toFixed(0)} (${formatSpeed(record?.triggerSpeedKph)})` : "Not available"} />
        <DetailRow label="Above Trigger" value={Number.isFinite(Number(record?.amountOverTriggerKph)) ? `+${Number(record.amountOverTriggerKph).toFixed(1)} km/h` : "Not available"} />
        <DetailRow label="Duration" value={formatDurationSeconds(record?.durationSeconds)} />
        <DetailRow label="Review Status" value={record?.reviewStatus || "pending"} />
      </div> : <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <DetailRow label="Offline Started At" value={formatDateTime(record?.offlineStartedAt || record?.startTime)} />
        <DetailRow label="Printed At" value={formatDateTime(record?.printedAt || record?.checkpointTime)} />
        <DetailRow label="Offline Duration" value={formatDurationSeconds(record?.offlineDurationSeconds)} />
        <DetailRow label="Gap Minutes" value={record?.gapMinutes != null ? `${record.gapMinutes} minutes` : "Not available"} />
        <DetailRow label="Telemetry Gap" value={formatDurationSeconds(record?.telemetryGapSeconds)} />
        <DetailRow label="Classification" value={record?.classification || record?.outageStatus || record?.category} />
      </div>}

      {record?.latestActionAt ? <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <DetailRow label="Latest Action" value={getInfringementActionLabel(record?.latestActionType)} />
        <DetailRow label="Action Time" value={formatDateTime(record?.latestActionAt)} />
        <DetailRow label="Action Note" value={record?.latestActionNote} />
      </div> : null}

      {canLogAction ? <div className="rounded-2xl border border-[#ece5dd] bg-[#fcfaf7] p-4">
        <div className="text-sm font-semibold uppercase tracking-[0.14em] text-[#8c7b6b]">Driver Action Log</div>
        <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-3">
          <Select
            value={actionType}
            onChange={setActionType}
            options={ACTION_OPTIONS}
            className="xl:col-span-1"
          />
          <Input.TextArea
            rows={3}
            value={actionNote}
            onChange={(event) => setActionNote(event.target.value)}
            placeholder={`Write how this ${record?.type || "infringement"} was handled.`}
            className="xl:col-span-2"
          />
        </div>
        <div className="mt-3 flex justify-end">
          <Button type="primary" loading={actionSaving} disabled={!actionNote.trim()} onClick={submitAction}>Log action on driver profile</Button>
        </div>
      </div> : null}

      {canReview ? <>
        <div className="flex flex-col gap-3">
          <span className="text-sm font-semibold uppercase tracking-[0.14em] text-[#8c7b6b]">Review Note</span>
          <Input.TextArea
            rows={4}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Add review context for this infringement."
          />
        </div>

        <div className="flex flex-wrap gap-3">
          <Button type="primary" loading={saving} onClick={() => submit({ reviewStatus: "accepted", reviewNote: note })}>Accept</Button>
          <Button loading={saving} onClick={() => submit({ reviewStatus: "dismissed", reviewNote: note })}>Dismiss</Button>
          <Button loading={saving} onClick={() => submit({ reviewStatus: "uncertain", reviewNote: note })}>Mark uncertain</Button>
          <Button loading={saving} onClick={() => submit({ reviewNote: note })}>Add note</Button>
        </div>
      </> : null}
    </div>}
  </Modal>;
};
